import { Chess, type Color, type Move, type Square } from 'chess.js';
export type { Color, Move, Square } from 'chess.js';
export type Promotion = 'q' | 'r' | 'b' | 'n';
export function parseUci(raw: string): { from: Square; to: Square; promotion?: Promotion } {
  const uci = raw.trim().toLowerCase();
  if (!/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(uci) || uci.slice(0, 2) === uci.slice(2, 4))
    throw new Error(`Invalid UCI: ${raw}`);
  return {
    from: uci.slice(0, 2) as Square,
    to: uci.slice(2, 4) as Square,
    ...(uci[4] ? { promotion: uci[4] as Promotion } : {}),
  };
}
export const normalizeUci = (raw: string) => {
  const m = parseUci(raw);
  return m.from + m.to + (m.promotion ?? '');
};
export const toUci = (m: Move) => m.from + m.to + (m.promotion ?? '');
export type Result =
  'checkmate' | 'stalemate' | 'repetition' | 'insufficient-material' | 'fifty-move' | 'draw';
/** The sole rules boundary. Continuation retains one instance to preserve repetition history. */
export class ChessRules {
  private game: Chess;
  constructor(fen?: string) {
    this.game = new Chess(fen);
  }
  get fen() {
    return this.game.fen();
  }
  get turn(): Color {
    return this.game.turn();
  }
  get check() {
    return this.game.isCheck();
  }
  get mate() {
    return this.game.isCheckmate();
  }
  get result(): Result | undefined {
    if (this.game.isCheckmate()) return 'checkmate';
    if (this.game.isStalemate()) return 'stalemate';
    if (this.game.isThreefoldRepetition()) return 'repetition';
    if (this.game.isInsufficientMaterial()) return 'insufficient-material';
    if (this.game.isDrawByFiftyMoves()) return 'fifty-move';
    if (this.game.isDraw()) return 'draw';
  }
  legal(square?: Square) {
    return this.game.moves({ verbose: true, ...(square ? { square } : {}) });
  }
  piece(square: Square) {
    return this.game.get(square);
  }
  get checkSquare() {
    return this.check
      ? this.game
          .board()
          .flat()
          .find((p) => p?.type === 'k' && p.color === this.turn)?.square
      : undefined;
  }
  play(uci: string) {
    return this.game.move(parseUci(uci));
  }
  tryMove(uci: string): Move | undefined {
    try {
      return this.play(uci);
    } catch {
      return undefined;
    }
  }
  clone() {
    const copy = new ChessRules(this.game.history({ verbose: true })[0]?.before ?? this.fen);
    for (const move of this.game.history({ verbose: true })) copy.play(toUci(move));
    return copy;
  }
}
